import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import { type Country } from "react-phone-number-input/input";

import { formatPhone } from "./helpers.utils";

/**
 * When the user picked a country in the UI, parses national input to E.164 when possible.
 * Otherwise returns the trimmed raw string (partial input stays editable).
 */
export function normalizePhoneInputToE164IfPossible(raw: string, selectedCountry: Country): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed.startsWith("+")) {
    const parsed = parsePhoneNumberFromString(trimmed);
    if (parsed?.isPossible()) {
      return parsed.number;
    }
    return trimmed;
  }

  const parsedNational = parsePhoneNumberFromString(trimmed, selectedCountry);
  if (parsedNational?.isPossible()) {
    return parsedNational.number;
  }

  return trimmed;
}

const NANP_FALLBACK_COUNTRIES: readonly Country[] = ["US", "CA"];

function parseValidNanpNumber(value: string) {
  for (const country of NANP_FALLBACK_COUNTRIES) {
    const parsedNumber = parsePhoneNumberFromString(value, country);
    if (parsedNumber?.isValid()) {
      return parsedNumber;
    }
  }

  return undefined;
}

/**
 * Parses input as international digits typed without "+" (e.g. "447911123456").
 * Only bare digits (with optional spaces) qualify: input carrying national
 * formatting like "(434) 343-434" must not be re-interpreted as a different
 * country's number by stripping its punctuation ("+434343434" is a valid
 * Austrian number, which made 9-digit US partials pass validation).
 */
function parseBareInternationalNumber(trimmed: string) {
  if (!/^\d[\d ]*$/.test(trimmed)) {
    return undefined;
  }

  return parsePhoneNumberFromString(`+${trimmed.replaceAll(" ", "")}`);
}

/**
 * Validates whether a phone string is a complete international phone number.
 * Accepts E.164 (+1…), US national (10-digit), and bare international digits.
 */
export function isValidPhoneNumber(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  if (trimmed.startsWith("+")) {
    return parsePhoneNumberFromString(trimmed)?.isValid() ?? false;
  }

  if (parseValidNanpNumber(trimmed)) {
    return true;
  }

  return parseBareInternationalNumber(trimmed)?.isValid() ?? false;
}

/**
 * Normalizes a phone string to E.164 format when possible.
 * Falls back to US-formatted display when parsing fails.
 */
export function normalizePhoneToE164(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed.startsWith("+")) {
    return parsePhoneNumberFromString(trimmed)?.number ?? formatPhone(trimmed);
  }

  const nanpParsedNumber = parseValidNanpNumber(trimmed);
  if (nanpParsedNumber) {
    return nanpParsedNumber.number;
  }

  const internationalParsedNumber = parseBareInternationalNumber(trimmed);
  if (internationalParsedNumber?.isPossible()) {
    return internationalParsedNumber.number;
  }

  return formatPhone(trimmed);
}

/**
 * Renders a stored E.164 number for a human to read back — "+1 718 614 7604" rather than
 * "+17186147604". Display only; never feed the result back to the API.
 */
export function formatPhoneForDisplay(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  // An unparseable number is shown exactly as stored: a confirmation screen must echo the real
  // value, so guessing at a prettier form would be worse than the raw one.
  return parsePhoneNumberFromString(trimmed)?.formatInternational() ?? trimmed;
}
